import "../styles/modes.css";
import { LevelCatalog, Modes } from "@highmarch/app";
import { isoWeekNumber, utcDay } from "@highmarch/domain";
import { btnHtml } from "../kit";
import { icon } from "../iconsKit";
import { checkHtml, sheetHeadHtml, wellHtml } from "../components";
import { bindRewardFocus, dotHtml, evBannerHtml, evChipHtml, evKickHtml, rewardTileHtml } from "../hub/evKit";
import { SHOP_SKIN } from "../shopSkin";
import { paidContents, rewardChest } from "../chestReveal";
import { toast } from "../toast";
import { paidLine, rewardArt, rewardWords, rewardWordsHtml } from "../hub/eventRewards";
import { fmtLeft } from "../hub/eventsSheet";
import { econCue } from "../hub/purchaseFx";
import { paintedIconHtml } from "../featureIcons";
import { bindMilestoneClaims, milestoneTrackHtml } from "../forge/milestoneTrack";

/** What the Endless sheet needs. */
export interface EndlessSheetCtx {
  readonly catalog: LevelCatalog;
  readonly modes: Modes;
  readonly now: () => number;
  /** Saves and redraws the hub (the More badge). */
  readonly onChange: () => void;
  /** Starts an Endless run (the sheet goes first). */
  readonly start: () => void;
}

/** The sheet head's painted loop (CSS px), as Daily Road's. */
const HEAD_ICON_PX = 52;
const CLAIM_ART_PX = 56;


function pendingHtml(ctx: EndlessSheetCtx): string {
  const p = ctx.modes.pendingChest(ctx.now());
  const band = p ? ctx.modes.bandsFor(p.waves).band : null;
  if (!p || !band) return "";
  return `<section class="encard claim"><div class="wellbox">${wellHtml({ art: rewardArt(band.reward, CLAIM_ART_PX), rarity: band.reward.chest, size: "lg" })}</div>
    <div class="txt"><small class="disp">LAST WEEK · WAVE ${p.waves}</small><b class="disp">${rewardWordsHtml(band.reward, 16)}</b></div>${dotHtml(1, true)}
    ${btnHtml("CLAIM", "gold", { className: "disp claimweek" })}</section>`;
}

function ladderHtml(ctx: EndlessSheetCtx): string {
  const s = ctx.catalog.modes, { band } = ctx.modes.weekBand(ctx.now());
  return ctx.modes.data.endless.weeklyChest.map((b) => {
    const reached = s.weekWaves >= b.fromWave, current = band?.fromWave === b.fromWave;
    const foot = current ? `<span class="evfoot yours">YOURS</span>` : reached ? `<span class="evfoot got">${checkHtml()}</span>` : `<span class="evfoot lk">${icon("lock", 13)}</span>`;
    return rewardTileHtml(b.reward, { top: `${b.fromWave}+`, foot, dim: !reached, px: 38, className: `enrung${reached ? " reached" : ""}${current ? " current" : ""}` });
  }).join("");
}

function bodyHtml(ctx: EndlessSheetCtx): string {
  const s = ctx.catalog.modes, m = ctx.modes, now = ctx.now(), d = m.data.endless;
  const { band } = m.weekBand(now);
  const chips = `${evChipHtml(`${icon("clock", 13)}${fmtLeft(m.weekEndsAt(now) - now)}`)}${evChipHtml(`Week ${isoWeekNumber(utcDay(now))}`)}`;
  return `
    ${pendingHtml(ctx)}
    ${evBannerHtml({ scene: "evendless", title: "Endless", chips })}
    <div class="enplay"><div class="enbests">
      <div class="enbest week"><small class="disp">THIS WEEK</small><b class="disp">${s.weekWaves > 0 ? `Wave ${s.weekWaves}` : "—"}</b></div>
      <div class="enbest all"><small class="disp">ALL TIME</small><b class="disp">${s.bestWaves > 0 ? `Wave ${s.bestWaves}` : "—"}</b></div>
    </div>${btnHtml("PLAY", "gold", { icon: "play", className: "disp go endlessgo" })}</div>
    ${evKickHtml("WEEKLY CHEST", band ? rewardWords(band.reward) : `from wave ${d.weeklyChest[0]?.fromWave ?? 0}`)}
    <div class="enladder">${ladderHtml(ctx)}</div>
    ${milestonesHtml(ctx)}`;
}

/** Gear v2's free route: this week's Endless milestones (and last week's reached, unclaimed ones) with CLAIM. */
function milestonesHtml(ctx: EndlessSheetCtx): string {
  const rows = ctx.modes.endlessMilestones(ctx.now());
  if (rows.length === 0) return "";
  const ready = rows.filter((r) => r.reached && !r.claimed).length;
  return `${evKickHtml("MILESTONES", ready > 0 ? `${ready} to claim` : "every week")}
    ${milestoneTrackHtml(rows, "endless", ctx.catalog.modes.week)}`;
}

/**
 * More → Endless (docs/MODES.md): this week's and the all-time best, the weekly chest's ladder (the band this week's
 * best has reached), last week's chest to claim, the weekly milestones (gear v2's free route), and PLAY.
 */
export function showEndlessSheet(parent: HTMLElement, ctx: EndlessSheetCtx): HTMLElement {
  parent.querySelector(":scope > .sheet.endless")?.remove();
  const sheet = document.createElement("div");
  sheet.className = `sheet panel endless ${SHOP_SKIN}`;
  parent.append(sheet);
  const render = (): void => {
    sheet.innerHTML = `${sheetHeadHtml({ title: "Endless", portrait: paintedIconHtml("endless", HEAD_ICON_PX), tint: "plum" })}<div class="enbody">${bodyHtml(ctx)}</div>`;
    bindRewardFocus(sheet, parent, ctx.catalog.balance, ctx.catalog.highestLevelCleared);
    sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => sheet.remove();
    sheet.querySelector<HTMLButtonElement>(".endlessgo")!.onclick = () => { sheet.remove(); ctx.start(); };
    sheet.querySelector<HTMLButtonElement>(".claimweek")?.addEventListener("click", () => claim());
    bindMilestoneClaims(sheet, { balance: ctx.catalog.balance, layerHost: sheet, toastHost: parent },
      (_k, week, index) => { const c = ctx.modes.claimEndlessMilestone(week, index, ctx.now()); if (typeof c !== "string") ctx.onChange(); return c; },
      () => { if (sheet.isConnected) { const keep = sheet.querySelector<HTMLElement>(".enbody")?.scrollTop ?? 0; render(); const b = sheet.querySelector<HTMLElement>(".enbody"); if (b) b.scrollTop = keep; } });
  };
  const claim = (): void => {
    const c = ctx.modes.claimWeekly(ctx.now());
    if (!c) return;
    econCue("event");
    ctx.onChange();
    const line = paidLine(c.paid, c.gear);
    if (c.paid.chest) {
      const chest = c.paid.chest;
      rewardChest(sheet.parentElement ?? sheet, ctx.catalog.balance, chest.rarity, paidContents(c.paid, c.gear), line, render);   // M30 #10
      return;
    }
    toast(parent, line, 2200);
    render();
  };
  render();
  return sheet;
}

/** The chest a best of `waves` reaches, as words ("Rare chest + supplies"), or "" under the first band. */
export function bandWords(modes: Modes, waves: number): string {
  const b = modes.bandsFor(waves).band;
  return b ? rewardWords(b.reward) : "";
}

